/**
 * Tier model.
 *
 * One product, one design system, three tiers on a continuum (README § Product concept).
 * Locked features are NEVER hidden — they render with a lock icon and route to /pricing, so
 * the resident tier is the top of the upgrade funnel rather than a stripped-down app.
 *
 * This module is the single source of truth for what a tier can see. It is intentionally
 * declarative: entitlement is ALSO enforced server-side on every API call, and this exists to
 * drive the UI, not to protect anything.
 */

export type Tier = 'snapshot' | 'intelligence' | 'pro';

/** Everything the nav or a surface can gate on. */
export type Feature =
  | 'myTowns'
  | 'meetingDetail'
  | 'awareExplain'
  | 'search'
  | 'signals'
  | 'signalFeed'
  | 'legislativeHistory'
  | 'topicTrends'
  | 'askAware'
  | 'whatsAppAlerts';

/** Countries in the catalogue, with their own region vocabulary (README § 9). */
export type CountryCode = 'US' | 'CA' | 'GB' | 'AU' | 'NZ';

// There is deliberately NO per-seat concept here. Intelligence Pro was once "5 users" at the
// same $199 and is now single-seat, like the other two. A `perUser` flag survived that change
// for a while, false on every tier and still gated a " per user" suffix on three pricing
// surfaces — a string that could only ever reappear by accident. Removed rather than left
// false. If seats come back, they come back as a real quantity, not a boolean.
export interface TierDefinition {
  id: Tier;
  /** Customer-facing name. Never abbreviate these in UI. */
  label: string;
  /** USD monthly. The full multi-currency table lives with the pricing surface. */
  usdMonthly: number;
  /** Followed-town cap. `null` = unlimited. */
  townCap: number | null;
  /** Countries whose towns this tier may follow and search. */
  countries: readonly CountryCode[];
  /**
   * Monthly Aware Explain allowance. Separate from any purchased top-up balance — paid
   * balances carry over on plan change, allowances reset (BUSINESS_RULES rule 2).
   */
  awareExplainMonthly: number | null;
  /** Monthly Ask Aware allowance; `null` where the feature is locked entirely. */
  askAwareMonthly: number | null;
  features: readonly Feature[];
}

const CONSUMER: readonly Feature[] = ['myTowns', 'meetingDetail', 'awareExplain'];

/**
 * NOTE — unresolved in the handoff, flagged to the account owner:
 *
 * 1. Country gating. README § 9 says "Snapshot = US only" and then, in the same bullet,
 *    "Snapshot is gated to US/Canada specifically." § 11 (Pricing) supports US + Canada,
 *    since Snapshot goes unavailable in any non-US/CA currency. Encoded as US + CA below.
 *
 * 2. RESOLVED 08/14/2026 by the account owner: Intelligence Pro's Aware Explain allowance is
 *    800/mo. The handoff never stated it — the tier table gave Snapshot 100 and Intelligence
 *    400, then described Pro only as adding Ask Aware.
 */
export const TIERS: Record<Tier, TierDefinition> = {
  snapshot: {
    id: 'snapshot',
    label: 'Snapshot',
    usdMonthly: 12,
    townCap: 1,
    countries: ['US', 'CA'],
    awareExplainMonthly: 100,
    askAwareMonthly: null,
    features: CONSUMER,
  },
  intelligence: {
    id: 'intelligence',
    label: 'Intelligence',
    usdMonthly: 99,
    townCap: 3,
    countries: ['US', 'CA'],
    awareExplainMonthly: 400,
    askAwareMonthly: null,
    features: [...CONSUMER, 'search', 'signals', 'signalFeed', 'whatsAppAlerts'],
  },
  pro: {
    id: 'pro',
    label: 'Intelligence Pro',
    usdMonthly: 199,
    townCap: null,
    countries: ['US', 'CA', 'GB', 'AU', 'NZ'],
    awareExplainMonthly: 800,
    askAwareMonthly: 200,
    features: [
      ...CONSUMER,
      'search',
      'signals',
      'signalFeed',
      'whatsAppAlerts',
      'legislativeHistory',
      'topicTrends',
      'askAware',
    ],
  },
};

/** Tiers that get the denser, two-pane pro layout at >=1100px. */
export const PRO_TIERS: readonly Tier[] = ['intelligence', 'pro'];

export function can(tier: Tier, feature: Feature): boolean {
  return TIERS[tier].features.includes(feature);
}

export function isProTier(tier: Tier): boolean {
  return PRO_TIERS.includes(tier);
}

export function canFollowCountry(tier: Tier, country: CountryCode): boolean {
  return TIERS[tier].countries.includes(country);
}

/** `null` cap means unlimited — do not coerce it to a number. */
export function canFollowMoreTowns(tier: Tier, followedCount: number): boolean {
  const cap = TIERS[tier].townCap;
  return cap === null || followedCount < cap;
}

/**
 * My Feed is the landing route at EVERY tier — the prototype sets `home` to the towns feed
 * for resident, analyst and govaffairs alike. README § 2 calling Signals "the pro home"
 * describes where a pro user lives, not where they land.
 */
export function homeRoute(): '/feed' {
  return '/feed';
}
